import { ListItem } from '@lili/lili-frontend-common';
import {
  CountryData,
  IGetDefaultValues,
  IInternationalData,
  MoveMoneyTypes,
  UpdateVendorFormFieldsMap, UpdateVendorFormProps,
} from './interfaces';
import { ExtendedUseFormReturn } from '../../hooks/providers/useFormHooks';
import { vendorCreationTypesMap } from './consts';
import { removeSpacesFromString } from '../../services/general.service';

export const getDefaultValues = (props: IGetDefaultValues): UpdateVendorFormProps => {
  const { moveType, vendorData, preFilledBusinessName } = props;

  return {
    [UpdateVendorFormFieldsMap.BUSINESS_NAME]: preFilledBusinessName || vendorData?.name || '',
    [UpdateVendorFormFieldsMap.EMAIL]: vendorData?.email || '',
    [UpdateVendorFormFieldsMap.INTER_COUNTRY]: vendorData?.internationalWireAccountData?.internationalSupplierAddressData.country || '',
    [UpdateVendorFormFieldsMap.SWIFT]: vendorData?.internationalWireAccountData?.swiftCode || '',
    [UpdateVendorFormFieldsMap.IBAN]: vendorData?.internationalWireAccountData?.iban || '',
    [UpdateVendorFormFieldsMap.INTER_PHONE]: vendorData?.internationalWireAccountData?.internationalPhoneNumber || '',
    [UpdateVendorFormFieldsMap.INTER_PHONE_STATUS]: moveType !== MoveMoneyTypes.INTERNATIONAL_WIRE_OUT,
    [UpdateVendorFormFieldsMap.INTER_PHONE_REGION_CODE]: null,
    [UpdateVendorFormFieldsMap.INTER_LINE_1]: vendorData?.internationalWireAccountData?.internationalSupplierAddressData.line1 || '',
    [UpdateVendorFormFieldsMap.INTER_LINE_2]: vendorData?.internationalWireAccountData?.internationalSupplierAddressData.line2 || '',
    [UpdateVendorFormFieldsMap.INTER_CITY]: vendorData?.internationalWireAccountData?.internationalSupplierAddressData.city || '',
    [UpdateVendorFormFieldsMap.INTER_STATE]: vendorData?.internationalWireAccountData?.internationalSupplierAddressData.state || '',
    [UpdateVendorFormFieldsMap.INTER_POSTAL_CODE]: vendorData?.internationalWireAccountData?.internationalSupplierAddressData.postalCode || '',
    [UpdateVendorFormFieldsMap.LINE_1]: vendorData?.supplierAddressData?.line1 || '',
    [UpdateVendorFormFieldsMap.LINE_2]: vendorData?.supplierAddressData?.line2 || '',
    [UpdateVendorFormFieldsMap.CITY]: vendorData?.supplierAddressData?.city || '',
    [UpdateVendorFormFieldsMap.STATE]: vendorData?.supplierAddressData?.state || '',
    [UpdateVendorFormFieldsMap.POSTAL_CODE]: vendorData?.supplierAddressData?.postalCode || '',
    [UpdateVendorFormFieldsMap.ACH_ACCOUNT_NUMBER]: vendorData?.achAccountData?.accountNumber || '',
    [UpdateVendorFormFieldsMap.ACH_ROUTING_NUMBER]: vendorData?.achAccountData?.routingNumber || '',
    [UpdateVendorFormFieldsMap.WIRE_ACCOUNT_NUMBER]: vendorData?.wireAccountData?.accountNumber || '',
    [UpdateVendorFormFieldsMap.WIRE_ROUTING_NUMBER]: vendorData?.wireAccountData?.routingNumber || '',
    [UpdateVendorFormFieldsMap.NOTIFY_VENDOR]: vendorData?.notifyOnPayment || false,
  };
};

export const generateCountryPickerData = (countryData: CountryData) => {
  const list: ListItem[] = countryData.supportedCountries.countries.map(country => ({
    title: country.name,
    value: country.code,
  }));
  list.push({ title: 'Other countries', value: 'OTHER', persist: true });
  return list;
};

export const isInternationalDataValid = (intlData: IInternationalData | null | undefined) => {
  if (!intlData) return false;
  const { iban, swiftCode, internationalSupplierAddressData: { line1, city, country, postalCode } } = intlData;
  const mandatoryIntlData = [iban, swiftCode, line1, city, country, postalCode];

  return mandatoryIntlData.every((value) => value);
};

export const buildPayload = (formMethods: ExtendedUseFormReturn<UpdateVendorFormProps>, supplierId: string, formType: string, moveType: string) => {
  const formData = formMethods.getValues();

  const baseData = {
    supplierId: supplierId || null,
    supplierEntityType: formType,
    methodType: vendorCreationTypesMap[moveType as keyof typeof vendorCreationTypesMap],
    name: formData[UpdateVendorFormFieldsMap.BUSINESS_NAME],
    email: formData[UpdateVendorFormFieldsMap.EMAIL] || null,
    notifyOnPayment: formData[UpdateVendorFormFieldsMap.NOTIFY_VENDOR],
  };

  const achAccountData = {
    accountNumber: formData[UpdateVendorFormFieldsMap.ACH_ACCOUNT_NUMBER] || null,
    routingNumber: formData[UpdateVendorFormFieldsMap.ACH_ROUTING_NUMBER] || null,
  };

  const wireAccountData = {
    accountNumber: formData[UpdateVendorFormFieldsMap.WIRE_ACCOUNT_NUMBER] || null,
    routingNumber: formData[UpdateVendorFormFieldsMap.WIRE_ROUTING_NUMBER] || null,
  };

  const supplierAddressData = {
    city: formData[UpdateVendorFormFieldsMap.CITY],
    state: formData[UpdateVendorFormFieldsMap.STATE],
    line1: formData[UpdateVendorFormFieldsMap.LINE_1],
    line2: formData[UpdateVendorFormFieldsMap.LINE_2] || null,
    postalCode: formData[UpdateVendorFormFieldsMap.POSTAL_CODE],
    country: null,
  };

  const internationalWireAccountData = {
    swiftCode: formData[UpdateVendorFormFieldsMap.SWIFT],
    iban: removeSpacesFromString(formData[UpdateVendorFormFieldsMap.IBAN]),
    internationalPhoneNumber: formData[UpdateVendorFormFieldsMap.INTER_PHONE],
    phoneNumberCountryCode: formData[UpdateVendorFormFieldsMap.INTER_PHONE_REGION_CODE],
    internationalSupplierAddressData: {
      city: formData[UpdateVendorFormFieldsMap.INTER_CITY],
      state: formData[UpdateVendorFormFieldsMap.INTER_STATE] || null,
      line1: formData[UpdateVendorFormFieldsMap.INTER_LINE_1],
      line2: formData[UpdateVendorFormFieldsMap.INTER_LINE_2] || null,
      postalCode: formData[UpdateVendorFormFieldsMap.INTER_POSTAL_CODE],
      country: formData[UpdateVendorFormFieldsMap.INTER_COUNTRY],
    },
  };

  return {
    ...baseData,
    achAccountData: achAccountData.accountNumber && achAccountData.routingNumber ? { ...achAccountData } : null,
    wireAccountData: wireAccountData.accountNumber && wireAccountData.routingNumber ? { ...wireAccountData } : null,
    supplierAddressData: supplierAddressData.line1 && supplierAddressData.city && supplierAddressData.state && supplierAddressData.postalCode ? { ...supplierAddressData } : null,
    internationalWireAccountData: isInternationalDataValid(internationalWireAccountData) ? { ...internationalWireAccountData } : null,
  };
};

export const formFiledList = [UpdateVendorFormFieldsMap.BUSINESS_NAME,
  UpdateVendorFormFieldsMap.EMAIL,
  UpdateVendorFormFieldsMap.INTER_COUNTRY,
  UpdateVendorFormFieldsMap.SWIFT,
  UpdateVendorFormFieldsMap.IBAN,
  UpdateVendorFormFieldsMap.INTER_PHONE,
  UpdateVendorFormFieldsMap.INTER_PHONE_STATUS,
  UpdateVendorFormFieldsMap.INTER_PHONE_REGION_CODE,
  UpdateVendorFormFieldsMap.INTER_LINE_1,
  UpdateVendorFormFieldsMap.INTER_LINE_2,
  UpdateVendorFormFieldsMap.INTER_CITY,
  UpdateVendorFormFieldsMap.INTER_STATE,
  UpdateVendorFormFieldsMap.INTER_POSTAL_CODE,
  UpdateVendorFormFieldsMap.LINE_1,
  UpdateVendorFormFieldsMap.LINE_2,
  UpdateVendorFormFieldsMap.CITY,
  UpdateVendorFormFieldsMap.STATE,
  UpdateVendorFormFieldsMap.POSTAL_CODE,
  UpdateVendorFormFieldsMap.ACH_ACCOUNT_NUMBER,
  UpdateVendorFormFieldsMap.ACH_ROUTING_NUMBER,
  UpdateVendorFormFieldsMap.WIRE_ACCOUNT_NUMBER,
  UpdateVendorFormFieldsMap.WIRE_ROUTING_NUMBER,
  UpdateVendorFormFieldsMap.NOTIFY_VENDOR];
